import React, { ReactElement, useEffect } from 'react';
import { Flex, VStack } from '@cad-arc';
import { noop, isEqual } from 'lodash';
import { FormProvider, useForm } from 'react-hook-form';
import { EsriMapMultiselectField } from './esri-map-multiselect-field';
import { DeprecatedEsriMapFilterOptions, DeprecatedEsriMapFilterState } from '../types';
import { useApplicationType } from '../../common/hooks/use-application-type';

type EsriMapFiltersDropdownProps = {
    filterOptions: DeprecatedEsriMapFilterOptions;
    filterState: DeprecatedEsriMapFilterState;
    setFilterState: (filterState: DeprecatedEsriMapFilterState) => void;
};

export const EsriMapFiltersDropdownDeprecated = ({
    filterOptions,
    filterState,
    setFilterState,
}: EsriMapFiltersDropdownProps): ReactElement => {
    const { isFirstResponder } = useApplicationType();
    const {
        priorityOptions,
        dispatchAreaOptions,
        summaryCategoryOptions,
        subdivisionOptions,
        primarySubdivisionName,
        agencyTypeOptions,
    } = filterOptions;

    const methods = useForm({
        defaultValues: filterState,
    });

    const { watch } = methods;
    const formStateWatch = watch();

    useEffect(() => {
        if (!isEqual(filterState, formStateWatch)) {
            setFilterState(formStateWatch);
        }
    }, [formStateWatch, filterState, setFilterState]);

    return (
        <Flex
            backgroundColor="surface.foreground"
            width="280px"
            flexDirection="column"
            alignItems="flex-start"
            position="relative"
            data-testid="MAP_FILTERS_DROPDOWN"
        >
            <FormProvider {...methods}>
                <form onSubmit={noop} style={{ width: '100%' }}>
                    <VStack spacing="20px">
                        {priorityOptions?.length && (
                            <EsriMapMultiselectField
                                label="Priority"
                                name="priorities"
                                options={priorityOptions}
                            />
                        )}
                        {summaryCategoryOptions?.length && (
                            <EsriMapMultiselectField
                                label="Unit Status"
                                name="summaryCategories"
                                options={summaryCategoryOptions}
                            />
                        )}
                        {subdivisionOptions?.length && (
                            <EsriMapMultiselectField
                                label={primarySubdivisionName ?? 'Beat'}
                                name="subdivisions"
                                options={subdivisionOptions}
                            />
                        )}
                        {dispatchAreaOptions?.length && (
                            <EsriMapMultiselectField
                                label="Dispatch Area"
                                name="dispatchAreas"
                                options={dispatchAreaOptions}
                            />
                        )}
                        {isFirstResponder && agencyTypeOptions?.length && (
                            <EsriMapMultiselectField
                                label="Agency Class"
                                name="agencyTypesGlobalAttributeIds"
                                options={agencyTypeOptions}
                            />
                        )}
                    </VStack>
                </form>
            </FormProvider>
        </Flex>
    );
};
